import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import i18next from 'i18next';
import { Conversation } from '../conversation.js';
import { Exam } from '../exam.js';
import { SET_EXAM_METADATA_TASK_CLASS } from '../api/pending-tasks/set-exam-metadata.task.js';
import { isStructureConfirmed } from '../api/tools/file-assignment.js';

/** The actual "which class/course is this?" copy. */
function buildExamClassQuestionContent(lng: string, subject: string, grade?: string | null): string {
  const isEnglish = lng === 'en';
  const subjectLabel = String(i18next.t(`Subjects.${subject}`, { lng, defaultValue: subject }));

  return grade
    ? isEnglish
      ? `❗ **I already detected the subject "${subjectLabel}" and grade/level "${grade}" — but I still need the exact class/course label** (e.g. the letter a, b, c, … or the course type LK/GK). What is it?`
      : `❗ **Ich habe bereits das Fach „${subjectLabel}" und die Klasse/Stufe „${grade}" erkannt — mir fehlt aber noch die genaue Kurs- bzw. Klassenbezeichnung** (z. B. der Buchstabe a, b, c, … oder der Kurstyp LK/GK). Wie lautet sie?`
    : isEnglish
      ? `❗ **Which class/grade and course does this exam belong to?** Subject: "${subjectLabel}". Examples: 9a, 10b, Q1, LK 12, GK 11.`
      : `❗ **Welcher Klasse/Stufe und welchem Kurs gehört diese Klausur?** Fach: „${subjectLabel}". Beispiele: 9a, 10b, Q1, LK 12, GK 11.`;
}

/**
 * Whether the conversation still shows the teacher something they have to act on — an unconfirmed
 * `ConfirmationMessage` (structure / submission split) or a still-active `AssignmentProposalMessage`.
 *
 * The class question is HELD BACK while one of these is open, so the teacher is never asked two
 * different things at once. It is posted right after the agent's reply to whichever card they answer.
 *
 * Follows the same "open confirmation" convention the rest of the codebase uses (see the fallback
 * lookup in confirm-in-chat-agent.ts): unconfirmed means open. A card that is never confirmed
 * therefore holds the question back indefinitely — the teacher can still set the class by hand in the
 * exam details sidebar (`updateExamMutation`), which is the same fallback as before.
 */
async function hasOpenUserPrompt(conversationId: string): Promise<boolean> {
  const conversationCollection = await getCollection(Conversation);
  const conversation = await conversationCollection.findOne({ _id: conversationId } as any, {
    projection: { messages: 1 },
  } as any);
  const messages: any[] = Array.isArray((conversation as any)?.messages) ? (conversation as any).messages : [];

  return messages.some(
    message =>
      (message?._class === 'ConfirmationMessage' && !message?.confirmed) ||
      (message?._class === 'AssignmentProposalMessage' && message?.active !== false),
  );
}

/** How long an asked question stays "asked" before the teacher is reminded. Only reached by teachers
 * who kept talking without ever answering it — answering resolves the task and removes it entirely. */
const REASK_COOLDOWN_MS = 24 * 60 * 60 * 1000;

/**
 * Atomically claims the right to ask: stamps `askedAt`/`askCount` on the exam's open
 * `SetExamMetadataPendingTask` ONLY if it has never been asked or the cooldown has expired, and
 * reports whether this caller was the one that won.
 *
 * Claim-then-ask, rather than check-then-ask, because the check and the stamp used to be two separate
 * round-trips: two agent turns (or two subscription re-establishments) that interleaved between them
 * both saw "not asked yet" and both asked, which is how the teacher ended up with the identical
 * question twice in a row. A single conditional update cannot interleave with itself.
 */
async function claimExamClassAsk(examId: string): Promise<boolean> {
  const examCollection = await getCollection(Exam as any);
  const cutoff = new Date(Date.now() - REASK_COOLDOWN_MS).toISOString();

  const result = await examCollection.updateOne(
    {
      _id: examId,
      'examState.pendingTasks': {
        $elemMatch: {
          _class: SET_EXAM_METADATA_TASK_CLASS,
          classResolved: false,
          // `askedAt: null` also matches the field being absent. ISO-8601 UTC strings are all the same
          // shape, so a lexicographic `$lt` is a chronological one.
          $or: [{ askedAt: null }, { askedAt: { $lt: cutoff } }],
        },
      },
    } as any,
    {
      $set: { 'examState.pendingTasks.$.askedAt': new Date().toISOString() },
      $inc: { 'examState.pendingTasks.$.askCount': 1 },
    } as any,
  );

  return ((result as any)?.modifiedCount ?? 0) > 0;
}

/**
 * The class question's content, to be appended to the assistant message the caller is already about to
 * write — or null when there is nothing to ask right now. It shares that message rather than adding
 * one: at the only moment it is asked (the structure confirmation, see below) the agent's reply is
 * about what happens next in the pipeline, and the class is precisely what that next step is waiting
 * on, so it belongs in the same breath instead of as a second bubble behind it.
 *
 * Returns content only when all four hold, in this order:
 *  1. the teacher has no card open to answer (`hasOpenUserPrompt`) — no stacking two asks,
 *  2. the structure is confirmed — the guided upload sequence is behind them,
 *  3. the exam still has an unresolved class on its `SetExamMetadataPendingTask`,
 *  4. this call wins `claimExamClassAsk` — never asked before, or not for a day.
 *
 * Step 2 re-reads the exam FRESH from the DB rather than trusting a snapshot taken at the start of the
 * turn: a `set_metadata` call with `klasse` earlier in the SAME turn may have just resolved this exact
 * task, and a stale snapshot would ask about a class the teacher just finished providing.
 *
 * Called from `finalizeAgentTurn` (agent-loop.ts) — the shared finalization for both the user-facing
 * chat and the server-initiated workflow agent. The content is handed back rather than pushed here:
 * `persistAndEmitFinalTurn` ends every turn with a `$set` that REPLACES the whole `messages` array
 * from its own in-memory snapshot, which would silently wipe out any `$push` made mid-turn.
 */
export async function maybeBuildExamClassQuestion(
  examId: string,
  conversationId: string,
  lng: string,
): Promise<string | null> {
  try {
    if (await hasOpenUserPrompt(conversationId)) return null;

    const examCollection = await getCollection(Exam as any);
    const exam = await examCollection.findOne(
      { _id: examId } as any,
      { projection: { 'examState.pendingTasks': 1, 'examState.structureConfirmationState': 1 } } as any,
    );

    // NOT BEFORE THE STRUCTURE IS CONFIRMED. Up to that point the teacher is being walked through the
    // upload → split → structure sequence, and every card in it is something they must answer; a class
    // question thrown in between just queues up behind cards they have not reached yet. Confirming the
    // structure is the seam: the guided part is done, and the class is what the rest of the pipeline is
    // now waiting on (see `examClassPending` in jobs/job-conditions.ts).
    if (!isStructureConfirmed((exam as any)?.examState)) return null;

    const pendingTasks: any[] = Array.isArray((exam as any)?.examState?.pendingTasks)
      ? (exam as any).examState.pendingTasks
      : [];
    const task = pendingTasks.find(t => t?._class === SET_EXAM_METADATA_TASK_CLASS && t?.classResolved === false);
    if (!task) return null;

    if (!(await claimExamClassAsk(examId))) return null;

    logger.info({ examId, context: 'ExamClassQuestion' }, 'Asking exam class question in its own message');
    return buildExamClassQuestionContent(lng, String(task.subject ?? ''), task.grade ?? undefined);
  } catch (err) {
    logger.warn({ err, examId, context: 'ExamClassQuestion' }, 'Failed to check/build exam class question');
    return null;
  }
}
